import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { shallowEqual, useSelector } from 'react-redux';
import { safeWindowOpen } from '~/helpers/safeUrlOpen.ts';
import { ManagerRole } from '~/constants/clientTypes.ts';
import instructionUakdoWebPortal from '~/assets/docs/instruction_uakdo_web_portal.pdf';
import useGetUserRole from '~/hooks/useGetUserRole.ts';
import { resolveManagerGroups } from '~/modules/covenants/access.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { selectAccessToken } from '~/store/covenants/selectors.ts';
import {
  selectGuaranteesAdministrationAccessMap,
  selectGuaranteesManagerSupervisorInfoStatus,
  selectHasGuaranteesAmAccess,
} from '~/store/guarantees/selectors.ts';
import { fetchGuaranteesManagerSupervisorInfo } from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  selectHasSupervisorAdministrationAccess,
  selectHasSupervisorApplicationAccess,
  selectHasSupervisorBranchManagerAccess,
  selectHasSupervisorCallCenterOperatorAccess,
  selectHasSupervisorClientViewAccess,
  selectHasSupervisorEmailBlackListAccess,
  selectShouldFetchSupervisorInfo,
} from '~/store/users/selectors.ts';
import { fetchSupervisorInfo } from '~/store/users/thunks.ts';
import { GuaranteesFetchStatus } from '~/store/guarantees/types.ts';
import { theme } from '~/theme/index.ts';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';
import { useGuaranteesAdministrationAccessBootstrap } from '~/modules/supervisor-administration/useGuaranteesAdministrationAccessBootstrap.ts';
import { MANAGER_LAYOUT_PATHS } from './constants.ts';
import ManagerHeader from './header/ManagerHeader.tsx';
import ManagerLayoutShell from './ManagerLayoutShell.tsx';
import { buildManagerNavigation, getManagerLayoutBreadcrumbs } from './navigation.ts';
import { managerLayoutStyles } from './styles.ts';
import type { IManagerHeaderProps, IManagerLayoutProps, TManagerNavigationItem } from './types.ts';

const ManagerLayout: React.FC<IManagerLayoutProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isGuaranteesEnabled, isTasksEnabled } = useFeatureFlags();
  const accessToken = useSelector(selectAccessToken);
  const shouldFetchSupervisorInfo = useSelector(selectShouldFetchSupervisorInfo);
  const guaranteesSupervisorInfoStatus = useSelector(selectGuaranteesManagerSupervisorInfoStatus);
  const guaranteesAdministrationAccessMap = useSelector(
    selectGuaranteesAdministrationAccessMap,
    shallowEqual
  );
  const hasGuaranteesAmAccess = useSelector(selectHasGuaranteesAmAccess);
  const hasApplicationAccess = useSelector(selectHasSupervisorApplicationAccess);
  const hasAdministrationAccess = useSelector(selectHasSupervisorAdministrationAccess);
  const hasCallCenterOperatorAccess = useSelector(selectHasSupervisorCallCenterOperatorAccess);
  const hasBranchManagerAccess = useSelector(selectHasSupervisorBranchManagerAccess);
  const hasClientViewAccess = useSelector(selectHasSupervisorClientViewAccess);
  const hasEmailBlackListAccess = useSelector(selectHasSupervisorEmailBlackListAccess);
  const {
    canViewPersonalData,
    canViewReports,
    canViewServices,
    isManagerLogin,
    roles,
    canViewIdentification,
  } = useGetUserRole();
  const hasAuthToken = Boolean(accessToken);
  const canUseGuaranteesAdministration = isManagerLogin && hasAuthToken && isGuaranteesEnabled;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    if (accessToken && shouldFetchSupervisorInfo) {
      void dispatch(fetchSupervisorInfo({ token: accessToken }));
    }
  }, [accessToken, dispatch, shouldFetchSupervisorInfo]);

  useEffect(() => {
    if (accessToken && guaranteesSupervisorInfoStatus === GuaranteesFetchStatus.Idle) {
      void dispatch(fetchGuaranteesManagerSupervisorInfo());
    }
  }, [accessToken, dispatch, guaranteesSupervisorInfoStatus]);

  useGuaranteesAdministrationAccessBootstrap(canUseGuaranteesAdministration);

  const managerGroups = useMemo(
    () => resolveManagerGroups(roles, accessToken),
    [accessToken, roles]
  );
  const hasUakdoAdministratorRole = useMemo(
    () => Array.isArray(managerGroups) && managerGroups.includes(ManagerRole.uakdo_administrator),
    [managerGroups]
  );

  const navigation = useMemo<TManagerNavigationItem[]>(
    () =>
      buildManagerNavigation({
        showServices: isManagerLogin || canViewServices,
        showReports: canViewIdentification,
        showPersonalData: canViewPersonalData,
        showApplications: hasAuthToken && hasApplicationAccess,
        showAdministration: hasAuthToken && hasAdministrationAccess,
        showUsapokAdministration:
          canUseGuaranteesAdministration && guaranteesAdministrationAccessMap.usapok,
        showUmvnAdministration:
          canUseGuaranteesAdministration && guaranteesAdministrationAccessMap.umvn,
        showUoAdministration:
          canUseGuaranteesAdministration && guaranteesAdministrationAccessMap.uo,
        showUdbtfAdministration:
          canUseGuaranteesAdministration && guaranteesAdministrationAccessMap.udbtf,
        showCallCenterOperator: hasAuthToken && hasCallCenterOperatorAccess,
        showBranchManager: hasAuthToken && hasBranchManagerAccess,
        showEnterAsClient: hasAuthToken && hasClientViewAccess,
        showUakdoInstruction:
          hasUakdoAdministratorRole ||
          hasApplicationAccess ||
          hasClientViewAccess ||
          hasCallCenterOperatorAccess,
        showEmailBlackList: hasAuthToken && hasEmailBlackListAccess,
        showGuarantees: canUseGuaranteesAdministration && hasGuaranteesAmAccess,
        showMyTasks: isManagerLogin && hasAuthToken && isTasksEnabled,
      }),
    [
      canUseGuaranteesAdministration,
      canViewPersonalData,
      canViewReports,
      canViewServices,
      guaranteesAdministrationAccessMap,
      hasAdministrationAccess,
      hasApplicationAccess,
      hasAuthToken,
      hasBranchManagerAccess,
      hasCallCenterOperatorAccess,
      hasClientViewAccess,
      hasEmailBlackListAccess,
      hasGuaranteesAmAccess,
      hasUakdoAdministratorRole,
      isManagerLogin,
      isTasksEnabled,
    ]
  );
  const breadcrumbs = useMemo(
    () => getManagerLayoutBreadcrumbs(location.pathname),
    [location.pathname]
  );
  const handleNavigationSelect = (path: string) => {
    if (path === MANAGER_LAYOUT_PATHS.covenantsInstruction) {
      safeWindowOpen(instructionUakdoWebPortal);
    } else if (
      path === MANAGER_LAYOUT_PATHS.guaranteesStart &&
      location.pathname.startsWith('/guarantees/administrator')
    ) {
      navigate(MANAGER_LAYOUT_PATHS.guaranteesAdministratorStart);
    } else {
      navigate(path);
    }
  };
  const headerProps: IManagerHeaderProps = {
    breadcrumbs,
    currentPath: location.pathname,
    navigation,
    onBreadcrumbNavigate: navigate,
    onNavigationSelect: handleNavigationSelect,
  };

  return (
    <Box sx={managerLayoutStyles.layoutRoot(theme)}>
      <ManagerHeader {...headerProps} />
      <ManagerLayoutShell>{children ?? <Outlet />}</ManagerLayoutShell>
    </Box>
  );
};

export default ManagerLayout;
